import type { ReactElement, Ref } from 'react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import type { TextInputProps } from './TextInput';
import type { ClassNameProp } from '../common-types';
import { showError } from '../../lib';
import { useCombobox } from 'downshift';
import { ToggleLayer } from 'react-laag';
import type { ComboboxDropdownMenuProps } from './internal';
import {
  ComboboxDropdownMenu,
  ComboboxInputElement,
  DEFAULT_COMBOBOX_LAYER_PLACEMENT
} from './internal';
import _ from 'lodash';

/**
 * Props of a {@link LiveSearch} component.
 */
export interface LiveSearchProps<Item>
  extends Pick<
      TextInputProps,
      'disabled' | 'debounceWait' | 'label' | 'placeholder' | 'labelOnTop'
    >,
    ClassNameProp,
    Partial<
      Pick<ComboboxDropdownMenuProps<Item>, 'renderItem' | 'renderEmptyItems'>
    > {
  /**
   * Mapping function for `Item` to distinguish items.
   * @param item the item to map
   */
  itemToKey: (item: Item) => string;
  /**
   * Mapping function for `Item` to create a human-readable label
   * @param item the item to map
   */
  itemToString: (item: Item) => string;
  /**
   * Callback handler for when an item is selected.
   * @param selectedItem the selected item
   */
  onChange: (selectedItem: Item) => void;
  /**
   * Ajax call used to load the selectable items when the input element's content changes.
   *
   * NOTE: This needs to be a cancellable ajax request which automatically cancels its previous request(s) when starting
   * a new request if you want it to behave in a sane way.
   * @param query
   */
  loadItems: (query?: string) => Promise<Item[]>;
  /**
   * Initial value to search by.
   * Setting/changing this will automatically trigger a request to loadItems with the initialSearchValue as the query.
   */
  initialSearchValue?: string;
  /**
   * When loadItems returns only a single item in its result set, this determines if that item
   * should automatically be selected or not.
   */
  automaticallySelectSingleResult?: boolean;
  /**
   * CSS classes to add to the downshift menu.
   */
  menuClassName?: string;
  /**
   * The min length of the query string, that determines if loadItems actually gets
   * executed or not. The default value is 1.
   * Setting this value to 0 will make to component automatically trigger the search when
   * it is rendered and display a list of all unfiltered items when it is focused.
   */
  minQueryLength?: number;
}

/**
 * Interface of a handler which gives parent component the control of the inner state
 */
export type LiveSearchElement = HTMLInputElement & {
  /**
   * clear selection
   */
  deselectCurrent: () => void;
  /**
   * Reset the selection, clear loaded items and input field
   */
  reset: () => void;
};

export type LiveSearchPropsWithStandardRef<Item> = LiveSearchProps<Item> & {
  ref?: Ref<HTMLInputElement>;
};

export type LiveSearchWithForwardRef<Item> = LiveSearchProps<Item> & {
  forwardedRef: Ref<HTMLInputElement>;
};

/**
 * Select style component which is meant to be used as a live AJAX search, which changes
 * its selectable items depending on the result of the AJAX query.
 *
 * @param properties a {@link LiveSearchProps} object
 * @return the created {@link JSX.Element}.
 * @constructor
 */
function LiveSearchComponent<Item>({
  itemToKey,
  itemToString,
  onChange,
  loadItems,
  debounceWait = 1000,
  label,
  disabled,
  placeholder,
  renderItem,
  renderEmptyItems,
  initialSearchValue,
  className = '',
  menuClassName = '',
  automaticallySelectSingleResult = false,
  minQueryLength = 1,
  forwardedRef,
  labelOnTop,
  ...props
}: Readonly<LiveSearchWithForwardRef<Item>>): JSX.Element {
  // TODO: maybe add some caching? e.g. result of the last 20 queries
  const [loading, setLoading] = useState(false);
  const [selectableItems, setSelectableItems] = useState<Array<Item>>([]);
  // local state of the text entered into the input field
  const [inputValue, setInputValue] = useState<string | undefined>('');
  // indicator that loadItems was called at least once before.
  const [wasLoaded, setWasLoaded] = useState(false);
  // indicator of first search. In case of initial search which results in one item,
  // we want it to be automatically selected independently of automaticallySelectSingleResult
  const [isFirstSearch, setIsFirstSearch] = useState(true);
  // we remember the most recent query, to prevent querying nonsensical querying
  const [previousSearchQuery, setPreviousSearchQuery] = useState<
    string | undefined
  >(undefined);
  // loading callbacks
  const inputValueChangeCallback = useCallback(
    (
      inputValue: string | undefined,
      previousSearchQuery: string | undefined
    ) => {
      if (
        (inputValue ?? '').length >= minQueryLength &&
        (previousSearchQuery === undefined ||
          inputValue !== previousSearchQuery)
      ) {
        setPreviousSearchQuery(inputValue);
        setLoading(true);
        loadItems(inputValue)
          .then(items => {
            setSelectableItems(items);
            setWasLoaded(true);
            setIsFirstSearch(false);
          })
          .catch(showError)
          .finally(() => setLoading(false));
      } else if (inputValue === '') {
        setPreviousSearchQuery(inputValue);
        setSelectableItems([]);
        setWasLoaded(false);
      }
    },
    [loadItems, minQueryLength]
  );
  const debouncedInputValueChangeCallback = useMemo(
    () => _.debounce(inputValueChangeCallback, debounceWait),
    [debounceWait, inputValueChangeCallback]
  );

  const deselectCurrent = () => {
    if (selectedItem) {
      selectItem((null as unknown) as Item);
    }
  };

  const reset = () => {
    // after resetting, the previousSearchQuery is treated as being the initial value, meaning undefined
    inputValueChangeCallback('', undefined);
    setInputValue('');
    selectItem((null as unknown) as Item);
  };

  // imperativeHandle to put the input element into the forwardedRef
  // to have access to it from the outside
  const inputElemRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(forwardedRef, () => ({
    ...(inputElemRef.current as HTMLInputElement),
    deselectCurrent,
    reset
  }));

  useEffect(() => {
    // when the initialSearchValue changes, we override the value of the input
    // element with the new initialSearchValue, and immediately do the change callback
    // but only if the initialSearchValue is different from what's already entered in the input element
    if (initialSearchValue && initialSearchValue !== inputValue) {
      inputValueChangeCallback(initialSearchValue, undefined);
      setInputValue(initialSearchValue);
    }
    // cheating dependencies since we don't want to depend on the `inputValue`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearchValue, inputValueChangeCallback]);

  useEffect(() => {
    // if the minQueryLength is explicitly set to 0, we trigger the search as soon as the component is rendered
    // so that the list of results is displayed immediately when opened without the need to type
    if (minQueryLength === 0 && !initialSearchValue) {
      inputValueChangeCallback('', undefined);
    }
  }, [initialSearchValue, inputValueChangeCallback, minQueryLength]);

  // handler for when the input value changes
  // this is a workaround to get around a Downshift bug (https://github.com/downshift-js/downshift/issues/1108)
  // (instead of using useCombobox.stateChangeTypes.InputChange in the switch further down)
  // TODO: remove the workaround once Downshift fixed the issue on their side
  const onInputChange = useCallback(
    value => {
      debouncedInputValueChangeCallback(value, previousSearchQuery);
      setInputValue(value);
    },
    [debouncedInputValueChangeCallback, previousSearchQuery]
  );

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    selectItem,
    selectedItem,
    toggleMenu,
    closeMenu,
    openMenu
  } = useCombobox({
    inputValue,
    items: selectableItems,
    defaultIsOpen: false,
    initialIsOpen: false,
    // "type" is missing in the types of onStateChange which is why this ignore is needed
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    onStateChange: ({ type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            selectItem(selectedItem);
            setInputValue(itemToString(selectedItem));
            onChange(selectedItem);
          }
          break;
        case useCombobox.stateChangeTypes.InputBlur:
          break;
        default:
          break;
      }
    },
    scrollIntoView(item) {
      item?.scrollIntoView({ block: 'nearest' });
    }
  });
  // effect for automatically selecting an item when the selectableItems only contains 1 item
  // or, in case of automatic selection is not enabled, when the initial search results in 1 item
  useEffect(() => {
    if (
      selectableItems.length === 1 &&
      (automaticallySelectSingleResult ||
        (initialSearchValue &&
          isFirstSearch &&
          inputValue === initialSearchValue))
    ) {
      // do the same as when clicking an item
      const itemToSelect = selectableItems[0];
      selectItem(itemToSelect);
      setInputValue(itemToString(itemToSelect));
      onChange(itemToSelect);
      // make sure to close the menu after selecting the item
      closeMenu();
    }
  }, [
    automaticallySelectSingleResult,
    closeMenu,
    initialSearchValue,
    inputValue,
    isFirstSearch,
    itemToString,
    onChange,
    selectItem,
    selectableItems
  ]);

  const selectedItems = [selectedItem].filter(value => !!value);
  return (
    <ToggleLayer
      isOpen={isOpen}
      closeOnOutsideClick={true}
      placement={DEFAULT_COMBOBOX_LAYER_PLACEMENT}
      renderLayer={({ isOpen, layerProps, triggerRect }): JSX.Element => (
        <ComboboxDropdownMenu<Item>
          className={menuClassName}
          loading={loading}
          selectableItems={selectableItems}
          selectedItems={selectedItems}
          highlightedIndex={highlightedIndex}
          itemToKey={itemToKey}
          renderItem={renderItem ?? itemToString}
          layerProps={layerProps}
          triggerRect={triggerRect}
          isOpen={isOpen}
          getMenuProps={getMenuProps}
          getItemProps={getItemProps}
          wasLoaded={wasLoaded}
          renderEmptyItems={renderEmptyItems}
          hideClearIcon={true}
        />
      )}
    >
      {({ triggerRef, isOpen }): JSX.Element => (
        <ComboboxInputElement<Item>
          {...props}
          ref={inputElemRef}
          className={className}
          triggerRef={triggerRef}
          variant="single"
          showSearchIcon={true}
          showExpandTool={false}
          selectedItems={selectedItems}
          isOpen={isOpen}
          label={label}
          itemToString={itemToString}
          disabled={disabled}
          toggleMenu={toggleMenu}
          openMenu={openMenu}
          getLabelProps={getLabelProps}
          getInputProps={getInputProps}
          placeholder={placeholder}
          getComboboxProps={getComboboxProps}
          elementClickBehavior="openMenu"
          onInputChange={onInputChange}
          removeSelectedItem={reset}
          labelOnTop={labelOnTop}
        />
      )}
    </ToggleLayer>
  );
}

export const LiveSearch: <Item>(
  props: Readonly<LiveSearchPropsWithStandardRef<Item>>
) => ReactElement | null = forwardRef(function LiveSearch(props, ref) {
  return <LiveSearchComponent forwardedRef={ref} {...props} />;
});
